import Axhelper from "@/lib/Axhelper";

class ResourcesService {
  constructor() {
    this.baseUrl = window.RUNTIME_CONFIG.API_URL;
    this.endpoints = {
      resources: `/api/resources/filters`,
      resourcesDelete: `/api/resources/`,
      resourcesCreate: `/api/resources/`,
      resourcesUpdate: `/api/resources/`
    };
  }

  async getById(id) {
    try {
      if (!id) {
        return {
          data: null,
          error: 'Invalid ID provided'
        };
      }

      // Use the internal getWithFilters method with id filter
      const result = await this.getWithFilters({
        limit: 1,
        filters: { id }
      });

      if (result.error) {
        return {
          data: null,
          error: result.error
        };
      }

      // Return the first item if found
      if (result.data && result.data.length > 0) {
        return {
          data: result.data[0],
          error: null
        };
      }

      return {
        data: null,
        error: 'Record not found'
      };
    } catch (error) {
      console.error('ResourcesService.getById error:', error);
      return {
        data: null,
        error: error.message || 'Failed to fetch record'
      };
    }
  }

  async getWithFilters(options = {}) {
    // console.log("TCL -> ResourcesService -> getWithFilters -> options:", options)
    
    try {
      const {
        limit = 50,
        sortColumn = 'created',
        sortDirection = 'desc',
        filters = {}
      } = options;

      const url = this._buildFilteredUrl({
        limit,
        sortColumn,
        sortDirection,
        filters
      });

      const response = await Axhelper.sendGetAsync(url);
      // console.log("TCL -> ResourcesService -> getWithFilters -> response:", response)

      if (response.error) {
        return {
          data: [],
          error: response.error,
          totalCount: 0,
          hasMore: false
        };
      }

      // Parse pagination info from headers
      const totalCount = response.headers?.['x-total-count']
        ? parseInt(response.headers['x-total-count'], 10)
        : response.data?.length || 0;

      const hasMore = response.data?.length < totalCount;

      return {
        data: response.data || [],
        error: null,
        totalCount,
        hasMore,
        headers: response.headers
      };
    } catch (error) {
      console.error('ResourcesService.getWithFilters error:', error);
      return {
        data: [],
        error: error.message || 'Failed to fetch records',
        totalCount: 0,
        hasMore: false
      };
    }
  }

  /**
   * Delete a record
   * @param {string} id - The record ID to delete
   * @returns {Promise<{success: boolean, error: string|null, message: string}>}
   */
  async delete(id) {
    try {
      if (!id) {
        return {
          success: false,
          error: 'Invalid ID provided',
          message: null
        };
      }

      const url = `${this.endpoints.resourcesDelete}${encodeURIComponent(id)}`;
      const response = await Axhelper.sendDeleteAsync(url);

      if (response.error) {
        return {
          success: false,
          error: response.error,
          message: null
        };
      }

      return {
        success: true,
        error: null,
        message: response.data || 'Record deleted successfully'
      };
    } catch (error) {
      console.error('ResourcesService.delete error:', error);
      return {
        success: false,
        error: error.message || 'Failed to delete record',
        message: null
      };
    }
  }

  async create(data) {
    try {
      const response = await Axhelper.sendPostAsync(this.endpoints.resourcesCreate, data);
      if (response.error) {
        return {
          success: false,
          error: response.error
        };
      }
      return {
        success: true,
        error: null,
        data: response.data || null
      };
    } catch (error) {
      console.error('ResourcesService.create error:', error);
      return {
        success: false,
        error: error.message || 'Failed to create record'
      };
    }
  }

  async update(id, data) {
    try {
      if (!id) {
        return {
          success: false,
          error: 'Invalid ID provided'
        };
      }

      const url = `${this.endpoints.resourcesUpdate}/${encodeURIComponent(id)}`;
      const response = await Axhelper.sendPutAsync(url, data);

      if (response.error) {
        return {
          success: false,
          error: response.error
        };
      }

      return {
        success: true,
        error: null,
        data: response.data || null
      };
    } catch (error) {
      console.error('ResourcesService.update error:', error);
      return {
        success: false,
        error: error.message || 'Failed to update record'
      };
    }
  }

  /**
   * Build URL for filtered requests
   * @private
   */
  _buildFilteredUrl({ limit, sortColumn, sortDirection, filters }) {
    let params = [
      `limit=${limit}`,
      `sortcol=${sortColumn}`,
      `sortdir=${sortDirection}`
    ];

    // Add id filter for specific record lookup
    if (filters.id) {
      params.push(`id=${encodeURIComponent(filters.id)}`);
    }

    // Add search text filter
    if (filters.searchText && filters.searchText.length >= 3) {
      params.push(`searchText=${encodeURIComponent(filters.searchText)}`);
    }

    return `${this.endpoints.resources}?${params.join('&')}`;
  }

}

// Export a singleton instance
export default new ResourcesService();